import React from "react";
import { Award, Users, Target, Heart } from "lucide-react";
import AnimatedSection from "../components/common/AnimatedSection";
import SectionHeading from "../components/common/SectionHeading";
import Button from "../components/common/Button";
import factoryImage from "../assets/images/factory.jpeg";
import panelImage from "../assets/images/PANEL.jpeg";
import factoryTeamImage from "../assets/images/factory-team.jpeg";

const timeline = [
  {
    year: "2024",
    title: "Founded",
    description:
      "Started with a vision to revolutionize interiors in Hyderabad.",
  },
  {
    year: "2024",
    title: "German Precision",
    description:
      "Invested in Biesse machines from Germany for factory-fit quality.",
  },
  {
    year: "2025",
    title: "Factory Setup",
    description: "Established our 10,000+ sq. ft manufacturing facility.",
  },
  {
    year: "2025",
    title: "Brigade Partnership",
    description: "Strategic partnership with Brigade Builder Group.",
  },
  {
    year: "2025",
    title: "Aparna Partnership",
    description: "Collaboration with Aparna Constructions for premium projects.",
  },
  {
    year: "2026",
    title: "Vue Partnership",
    description: "Partnered with Vue Properties to deliver luxury interiors.",
  },
];

const values = [
  {
    icon: Target,
    title: "Precision First",
    description:
      "Every cut, edge, and joint is measured to sub-millimeter accuracy. We believe precision is the foundation of lasting quality.",
  },
  {
    icon: Award,
    title: "Quality Materials",
    description:
      "Only MR, BWR, BWP Ply and HDHMR. No MDF shortcuts. We choose materials that last decades, not just years.",
  },
  {
    icon: Users,
    title: "Customer Partnership",
    description:
      "We don't just deliver interiors; we partner with you through the journey. Your vision becomes our mission.",
  },
  {
    icon: Heart,
    title: "Honest Pricing",
    description:
      "Transparent quotes with no hidden costs. What we promise is what you pay, from first estimate to final invoice.",
  },
];

const team = [
  {
    name: "Design Team",
    count: "8+",
    description:
      "Creative designers who transform your ideas into stunning visualizations.",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400&q=80",
  },
  {
    name: "Production Team",
    count: "15+",
    description:
      "Skilled technicians operating Biesse machines with precision.",
    image:
      panelImage,
  },
  {
    name: "Quality Team",
    count: "4+",
    description:
      "Dedicated inspectors ensuring every piece meets our standards.",
    image:
      factoryTeamImage,
  },
  {
    name: "Installation Team",
    count: "12+",
    description:
      "Professional installers who bring factory precision to your home.",
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&q=80",
  },
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1616137466211-f939a420be84?w=1920&q=80"
            alt="Iravatham Interiors"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/95 via-brand-primary/80 to-brand-primary/60" />
        </div>
        <div className="container-custom section-padding relative">
          <AnimatedSection className="max-w-3xl text-white">
            <span className="text-brand-accent text-sm font-medium uppercase tracking-wider">
              Our Story
            </span>
            <h1 className="font-heading text-display-1 mt-4 mb-6 text-white">
              Building better spaces since 2024.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              What started as a passion for quality interiors has grown into
              Hyderabad's trusted factory-first modular interiors company.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Story */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="relative">
                <div className="aspect-[4/3] rounded-3xl overflow-hidden">
                  <img
                    src={factoryImage}
                    alt="Our Factory"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-8 -right-8 bg-brand-accent rounded-2xl p-6 shadow-large">
                  <p className="text-4xl font-heading text-white">10,000+</p>
                  <p className="text-white/80">sq. ft factory</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-left">
              <SectionHeading
                label="Our Beginning"
                title="From vision to factory."
                align="left"
                className="mb-6"
              />
              <div className="space-y-4 text-text-secondary leading-relaxed">
                <p>
                  Iravatham Interiors was born from a simple observation: too
                  many homeowners were getting interiors that looked good
                  initially but failed within years. The culprits? Poor
                  materials, inconsistent craftsmanship, and no quality control.
                </p>
                <p>
                  We decided to change that. In 2024, we started with a focus on
                  quality materials – only MR, BWR, BWP Ply and HDHMR, never
                  MDF. But material quality alone wasn't enough. We needed
                  precision.
                </p>
                <p>
                  So we built our own factory in 2025. Today, our 10,000+ sq. ft
                  facility houses Biesse machines from Germany – the same
                  technology used by world-class furniture manufacturers. The
                  result? Factory-fit interiors with sub-millimeter precision,
                  delivered in 20 days.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <SectionHeading
            label="Our Journey"
            title="Milestones along the way."
            align="center"
          />

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <AnimatedSection key={item.year} delay={index * 100}>
                <div className="flex gap-6 mb-8 last:mb-0">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-brand-accent rounded-full flex items-center justify-center text-white font-heading">
                      {item.year.slice(-2)}
                    </div>
                    {index < timeline.length - 1 && (
                      <div className="w-0.5 h-full bg-border-light mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-8">
                    <span className="text-brand-accent text-sm font-medium">
                      {item.year}
                    </span>
                    <h4 className="font-heading text-xl text-brand-primary mt-1 mb-2">
                      {item.title}
                    </h4>
                    <p className="text-text-secondary">{item.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <SectionHeading
            label="What We Believe"
            title="Values that guide every project."
            align="center"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <AnimatedSection key={value.title} delay={index * 100}>
                <div className="text-center">
                  <div className="w-16 h-16 bg-brand-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-brand-accent" />
                  </div>
                  <h3 className="font-heading text-xl text-brand-primary mb-3">
                    {value.title}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <SectionHeading
            label="Our People"
            title="Dedicated teams for every step."
            align="center"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((dept, index) => (
              <AnimatedSection key={dept.name} delay={index * 100}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-soft">
                  <div className="aspect-[4/3]">
                    <img
                      src={dept.image}
                      alt={dept.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-2xl font-heading text-brand-accent">
                        {dept.count}
                      </span>
                      <span className="text-text-secondary text-sm">
                        members
                      </span>
                    </div>
                    <h4 className="font-heading text-lg text-brand-primary mb-2">
                      {dept.name}
                    </h4>
                    <p className="text-text-secondary text-sm">
                      {dept.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-brand">
        <div className="container-custom section-padding text-center">
          <AnimatedSection>
            <h2 className="font-heading text-h2 text-white mb-6">
              Let's build something beautiful together.
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Whether you're a homeowner dreaming of perfect interiors or a
              builder looking for a reliable partner, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button to="/contact" variant="white" size="large">
                Start Your Project
              </Button>
              <Button to="/factory" variant="outline" size="large">
                Visit Our Factory
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
